/**
 * Expert recommendation section: multi-select product cards added to the cart
 * in a single multi-item cart/add.js request (the same request shape
 * product-form.js uses for configurator extras, and the same post-add
 * choreography: cart icon bubble, cart drawer/page refresh, afterAtc setting).
 *
 * Member-price totals are resolved client-side from each card's
 * ilva.price_schedule JSON, reusing the globals main.js defines
 * (window.resolveScheduleState, window.formatMoney). main.js always loads
 * first: both scripts are deferred and theme.liquid's tag precedes any
 * section's.
 */
if (!customElements.get('expert-recommendation')) {
  class ExpertRecommendation extends HTMLElement {
    connectedCallback() {
      // The theme editor re-creates the element on section reload, so a fresh
      // instance runs init; the guard only protects against re-attachment.
      if (this.initialized) return;
      this.initialized = true;

      this.cta = this.querySelector('.js-er-cta');
      this.ctaLabel = this.querySelector('.js-er-cta-label');
      this.errorEl = this.querySelector('.js-er-error');
      this.memberRow = this.querySelector('.js-er-member-row');
      this.memberTotalEl = this.querySelector('.js-er-member-total');
      this.normalLabelEl = this.querySelector('.js-er-normal-label');
      this.normalTotalEl = this.querySelector('.js-er-normal-total');
      this.cartDrawer = document.querySelector('cart-drawer');

      this.entries = Array.from(this.querySelectorAll('.expert-rec__item')).reduce((entries, item) => {
        const dataEl = item.querySelector('.js-er-data');
        const checkbox = item.querySelector('.js-er-checkbox');
        if (!dataEl || !checkbox) return entries; // editor-only placeholder item

        let data;
        try {
          data = JSON.parse(dataEl.textContent);
        } catch (error) {
          return entries;
        }

        entries.push({ checkbox, data, member: ExpertRecommendation.resolveMemberPrice(data) });
        return entries;
      }, []);

      this.addEventListener('change', (evt) => {
        if (!evt.target.classList.contains('js-er-checkbox')) return;
        this.hideError();
        this.updateTotals();
      });

      if (this.cta) this.cta.addEventListener('click', this.addToCart.bind(this));

      this.updateTotals();
    }

    /**
     * Resolves today's member price for a card, in major units. Returns null
     * unless it genuinely undercuts the catalog price and its member row has
     * not ended — window.resolveScheduleState (main.js) owns that rule, so the
     * total can never disagree with the cards above it (ClickUp 869fbtgaa).
     * @param {object} data - Parsed .js-er-data payload.
     * @returns {number|null}
     */
    static resolveMemberPrice(data) {
      if (!data.schedule || typeof window.resolveScheduleState !== 'function') return null;
      const state = window.resolveScheduleState({ price: data.price, schedule: data.schedule });
      return state.memberActive ? state.memberMajor : null;
    }

    /**
     * Recomputes the totals row and the CTA label/state from the current
     * checkbox selection.
     */
    updateTotals() {
      const selected = this.entries.filter((entry) => entry.checkbox.checked);
      const count = selected.length;
      const normalTotal = selected.reduce((sum, entry) => sum + entry.data.price, 0) / 100;
      const hasMember = selected.some((entry) => entry.member !== null);
      const memberTotal = selected.reduce(
        (sum, entry) => sum + (entry.member !== null ? entry.member : entry.data.price / 100),
        0
      );

      if (this.memberRow) {
        this.memberRow.hidden = !hasMember;
        if (hasMember && this.memberTotalEl) this.memberTotalEl.textContent = window.formatMoney(memberTotal);
      }
      // "Ikke medlem" (non-member price) only makes sense as a counterpart to the member price;
      // with no member price in the selection the row is a plain total.
      if (this.normalLabelEl) {
        this.normalLabelEl.textContent = hasMember
          ? this.normalLabelEl.dataset.labelNormal
          : this.normalLabelEl.dataset.labelTotal;
      }
      if (this.normalTotalEl) this.normalTotalEl.textContent = window.formatMoney(normalTotal);

      if (this.cta) {
        this.cta.disabled = count === 0;
        const template = count === 1 ? this.cta.dataset.labelOne : this.cta.dataset.labelOther;
        if (this.ctaLabel && template) this.ctaLabel.textContent = template.replace('[count]', count);
      }
    }

    /**
     * Adds all selected variants to the cart in one request, then updates the
     * cart UI per the theme's "after add to cart" setting (product-form.js is
     * the reference for the response handling and timing).
     */
    async addToCart() {
      if (this.cta.getAttribute('aria-busy') === 'true') return;

      const items = this.entries
        .filter((entry) => entry.checkbox.checked)
        .map((entry) => ({ id: entry.data.variantId, quantity: 1 }));
      if (!items.length) return;

      this.hideError();
      this.cta.setAttribute('aria-busy', 'true');
      this.cta.classList.add('is-loading');

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const data = await response.json();

        if (!response.ok) {
          // The multi-item endpoint is atomic — nothing was added. Surface the
          // specific message (e.g. sold out) when Shopify provides one.
          const message = typeof data.description === 'string' ? data.description : data.message;
          this.showError(message);
          return;
        }

        this.cta.classList.add('is-success');

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        // Dispatch cart events for each added item so share-cart.js can attach
        // seller_id when the cart was empty at page load (matches product-form.js).
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach((resultItem) => {
            const itemInOldCart = oldCartData.items.find((item) => item.variant_id === resultItem.variant_id);
            if (itemInOldCart) {
              this.dispatchEvent(new CustomEvent('on:line-item:change', {
                bubbles: true,
                detail: {
                  variantId: resultItem.variant_id,
                  oldQuantity: itemInOldCart.quantity,
                  newQuantity: resultItem.quantity
                }
              }));
            } else {
              this.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                  variantId: resultItem.variant_id
                }
              }));
            }
          });
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete (matches product-form.js).
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }
      } catch (error) {
        this.showError();
      } finally {
        this.cta.removeAttribute('aria-busy');
        setTimeout(() => {
          this.cta.classList.remove('is-loading');
          this.cta.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Shows the error alert.
     * @param {string} [message] - Specific message; falls back to the generic
     *   cart error string.
     */
    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || theme.strings.cartError;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('expert-recommendation', ExpertRecommendation);
}
